import * as R from "remeda";

import type { DropdownItemData } from "../../components/common/DropdownHelpers";
import { ADVERTISER_ID_COLUMN } from "../../config/constants";
import { buildQueryParamFiltersFromDimensions } from "../../hooks/dimension/dimension.hooks";
import { DimensionStatus } from "../../store/dimension.status";
import type { DropdownDimensionValue } from "../../store/dimension.value";
import type { InputParameterFilter, Filter } from "../../types/common";
import { Comparator } from "../../types/common";
import type { Dimension } from "../../types/dimensions";
import { buildQueryParameterFilter } from "../../utils/app.utils";
import { getAdvertiserId } from "../../utils/permission.utils";

export interface ItemMapper extends Record<string, string> {
    label: string;
    value: string;
}

export interface RequestParameter {
    [x: string]: string;
}

export const processItemMapper = (
    data: Record<string, string>[],
    itemMapper: ItemMapper,
): DropdownItemData[] => {
    const itemMapPairs = Object.entries(itemMapper);
    const itemMapBuilder = (d: Record<string, string>): DropdownItemData =>
        R.pipe(
            itemMapPairs,
            R.map(([k, v]) => ({ [k]: d[v] })),
            R.mergeAll,
        ) as any;
    return R.pipe(data, R.map(itemMapBuilder));
};

export const getDropdownFilters = (
    inputParameterFilters: InputParameterFilter[] | undefined,
    searchFilter?: Filter,
    isAdvertiserScoped?: boolean,
): Filter[] | undefined => {
    const filters: Filter[] = [];
    if (isAdvertiserScoped) {
        filters.push({
            key: ADVERTISER_ID_COLUMN,
            value: getAdvertiserId()!,
            comparator: Comparator.EQUALS,
        });
    }
    if (inputParameterFilters && inputParameterFilters.length > 0) {
        // get all dimensions value filters
        filters.push(
            ...buildQueryParamFiltersFromDimensions(
                inputParameterFilters.filter((e) => R.isTruthy(e.dimension)),
            ),
        );
        // get all hard coded value filters
        filters.push(
            ...inputParameterFilters
                .filter((e) => R.isTruthy(e.value))
                .map((e) =>
                    buildQueryParameterFilter(
                        e.key,
                        e.condition,
                        e.value as any,
                        e.type,
                    ),
                ),
        );
    }
    if (searchFilter) {
        filters.push(searchFilter);
    }
    return filters.length > 0 ? filters : undefined;
};

export const buildUrl = (
    queryParameters: Array<string | undefined>,
): string => {
    return R.pipe(queryParameters, R.filter(R.isDefined), (x) => x.join("&"));
};

export const buildRequestParameter = (
    id: Dimension,
    v: DropdownDimensionValue,
): RequestParameter => ({
    [`${id}_id`]: v.value,
    [`${id}_name`]: v.label,
});

export const defaultRequestParameterMapping = (
    dimension: string,
): ItemMapper => ({
    value: `${dimension}_id`,
    label: `${dimension}_name`,
});

export const processDimensionStatus = (
    { value }: DropdownDimensionValue,
    optional: boolean,
): DimensionStatus => {
    if (optional) return DimensionStatus.SUCCESS;
    if (value.length === 0) {
        return DimensionStatus.EMPTY;
    }
    return DimensionStatus.SUCCESS;
};
